import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Stack, Tooltip, IconButton, TablePagination,
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import { Add, Info, VideoCall } from '@mui/icons-material';
import { attendanceService, leaveRequestService, attendanceRegularizationService, googleMeetService, holidayService } from '../services/api';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { usePagination } from '../hooks/usePagination';
import { AttendanceDayGrid, buildAttendanceDayRows } from './shared/AttendanceDayGrid';
import { RequestLeaveDialog } from './shared/RequestLeaveDialog';

const regStatusColor = (s: string) =>
  s === 'Approved' ? 'success' : s === 'Rejected' ? 'error' : 'warning';

const AttendanceTracking: React.FC = () => {
  const [attendance, setAttendance] = useState<any[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [holidays, setHolidays] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [regularizations, setRegularizations] = useState<any[]>([]);
  const { pagedRows: pagedRegularizations, paginationProps: regularizationPaginationProps } = usePagination(regularizations);
  const [message, setMessage] = useState('');
  const [msgSeverity, setMsgSeverity] = useState<'info' | 'success' | 'error'>('info');
  const [todayMarked, setTodayMarked] = useState(false);

  // Google Meet — gated behind attendance. canJoin/meetingUri are only ever trusted from the
  // backend response; disabling the button here is a UX nicety, not the actual security
  // mechanism (the backend re-validates on every /today/access call, including the one right
  // before opening the link).
  const [meetingExists, setMeetingExists] = useState(false);
  const [meetingAccess, setMeetingAccess] = useState<{ canJoin: boolean; meetingUri?: string; reason?: string } | null>(null);
  const [joining, setJoining] = useState(false);

  // "Request Leave" shortcut from a grid row — holds that row's date while the shared dialog
  // is open, null when closed.
  const [leaveDialogDate, setLeaveDialogDate] = useState<string | null>(null);

  // Regularization dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    requestedDate: '',
    requestedCheckIn: '',
    requestedCheckOut: '',
    requestedStatus: 'Present',
    reason: '',
  });

  const today = new Date().toISOString().split('T')[0];
  // Max date for the date picker = yesterday
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  const loadAttendance = useCallback(async () => {
    try {
      const data = await attendanceService.getMyAttendance();
      setAttendance(data);
      const marked = data.some((a: any) => a.date.split('T')[0] === today);
      setTodayMarked(marked);
    } catch {
      showMsg('Error loading attendance.', 'error');
    }
  }, [today]);

  const loadMeeting = useCallback(async () => {
    try {
      const [today, access] = await Promise.all([
        googleMeetService.getToday(),
        googleMeetService.getTodayAccess(),
      ]);
      setMeetingExists(!!today?.exists);
      setMeetingAccess(access);
    } catch {
      // Google Meet integration may not be connected/available — don't block attendance UI on it.
      setMeetingExists(false);
      setMeetingAccess(null);
    }
  }, []);

  const loadRegularizations = useCallback(async () => {
    try {
      const data = await attendanceRegularizationService.getMy();
      setRegularizations(data);
    } catch {
      // non-critical — silently ignore
    }
  }, []);

  const loadLeaveAndHolidays = useCallback(async () => {
    try {
      const [leaves, holidayList] = await Promise.all([
        leaveRequestService.getMyLeaveRequests(),
        holidayService.getHolidays(),
      ]);
      setLeaveRequests(leaves);
      setHolidays(holidayList);
    } catch {
      // non-critical — the grid below just shows fewer Leave/Holiday rows if this fails
    }
  }, []);

  useEffect(() => {
    loadAttendance();
    loadRegularizations();
    loadMeeting();
    loadLeaveAndHolidays();
  }, [loadAttendance, loadRegularizations, loadMeeting, loadLeaveAndHolidays]);

  const showMsg = (text: string, severity: 'info' | 'success' | 'error' = 'info') => {
    setMessage(text);
    setMsgSeverity(severity);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleMarkAttendance = async () => {
    try {
      const now = new Date();
      const checkInTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:00`;
      await attendanceService.markAttendance(checkInTime, 'Present');
      showMsg('Attendance marked successfully.', 'success');
      loadAttendance();
      loadMeeting();
    } catch (error: any) {
      showMsg(error.response?.data?.message || 'Error marking attendance.', 'error');
    }
  };

  const meetingAccessReasonText = (reason?: string) => {
    switch (reason) {
      case 'AttendanceRequired': return 'Mark your attendance above to unlock today\'s meeting.';
      case 'MeetingNotAvailable': return 'Today\'s meeting has not been created yet — check back shortly.';
      case 'EmployeeInactive': return 'Meeting access is unavailable for this account.';
      default: return 'Meeting access is currently unavailable.';
    }
  };

  const handleJoinMeeting = async () => {
    setJoining(true);
    try {
      // Re-check right before opening — never rely on the possibly-stale state from page load.
      const access = await googleMeetService.getTodayAccess();
      setMeetingAccess(access);
      if (access.canJoin && access.meetingUri) {
        window.open(access.meetingUri, '_blank', 'noopener,noreferrer');
      } else {
        showMsg(meetingAccessReasonText(access.reason), 'error');
      }
    } catch {
      showMsg('Could not verify meeting access. Please try again.', 'error');
    } finally {
      setJoining(false);
    }
  };

  const openRegDialog = (prefillDate = '') => {
    setForm({ requestedDate: prefillDate, requestedCheckIn: '', requestedCheckOut: '', requestedStatus: 'Present', reason: '' });
    setOpenDialog(true);
  };

  const handleSubmitRegularization = async () => {
    if (!form.requestedDate) return showMsg('Please select the date.', 'error');
    if (!form.requestedCheckIn) return showMsg('Check-in time is required.', 'error');
    if (!form.reason.trim()) return showMsg('Reason is required.', 'error');

    setSubmitting(true);
    try {
      await attendanceRegularizationService.submit({
        requestedDate: form.requestedDate,
        requestedCheckIn: form.requestedCheckIn,
        requestedCheckOut: form.requestedCheckOut || undefined,
        requestedStatus: form.requestedStatus,
        reason: form.reason,
      });
      showMsg('Regularization request submitted. Awaiting approval.', 'success');
      setOpenDialog(false);
      loadRegularizations();
    } catch (error: any) {
      showMsg(error.response?.data || 'Failed to submit request.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Same day-by-day grid design as the admin view (AdminAttendanceLeave) — only the action
  // differs by role: an admin can Reject a record, an employee gets a Regularize shortcut on
  // their own missing days instead.
  const monthStart = startOfMonth(parseISO(selectedMonth + '-01'));
  const dayRows = buildAttendanceDayRows(
    attendance,
    leaveRequests.filter(l => l.status === 'Approved'),
    holidays,
    { start: monthStart, end: endOfMonth(monthStart) }
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">Attendance Tracking</Typography>

      {message && (
        <Alert severity={msgSeverity} sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>
      )}

      {/* Marking attendance via the Virtual Office / Generate Meeting join flow now lives on its
          own page under the Meetings section — this card is the one way to mark attendance
          directly from the Attendance page. */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Mark Today's Attendance</Typography>
            <Typography variant="body2" color="text.secondary">{format(new Date(), 'EEEE, dd MMM yyyy')}</Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleMarkAttendance}
            disabled={todayMarked}
          >
            {todayMarked ? 'Attendance Already Marked' : 'Mark Attendance Now'}
          </Button>
        </Stack>
      </Paper>

      {/* Today's Google Meet — join is disabled until attendance is marked. The backend is the
          authoritative check (see handleJoinMeeting); this button state just mirrors it for UX.
          Hidden entirely when an admin has turned the integration off (Admin → Google Meet
          switch) — "GoogleMeetDisabled" is a distinct reason from "not available yet" and
          shouldn't show a confusing card for a feature that isn't in use. */}
      {(meetingExists || meetingAccess) && meetingAccess?.reason !== 'GoogleMeetDisabled' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Today's Meeting</Typography>
              <Typography variant="body2" color="text.secondary">
                {meetingAccess?.canJoin ? 'You can now join today\'s Google Meet.' : meetingAccessReasonText(meetingAccess?.reason)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              startIcon={<VideoCall />}
              onClick={handleJoinMeeting}
              disabled={!meetingAccess?.canJoin || joining}
            >
              {joining ? 'Checking…' : 'Join Google Meet'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Regularization section */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'warning.light', bgcolor: 'warning.50' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Missed Attendance?</Typography>
            <Typography variant="body2" color="text.secondary">
              Submit a regularization request for a past date. Your manager / admin will review it.
            </Typography>
          </Box>
          <Button variant="outlined" color="warning" startIcon={<Add />} onClick={() => openRegDialog()}>
            Request Regularization
          </Button>
        </Stack>
      </Paper>

      {/* Attendance History — same day-by-day grid as the admin view; missing days get a
          Regularize shortcut (admin's equivalent action there is Reject). */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={1}>
        <Typography variant="h6" fontWeight={600}>Attendance History</Typography>
        <TextField
          label="Select Month"
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Stack>
      <Box sx={{ mb: 4 }}>
        <AttendanceDayGrid
          rows={dayRows}
          renderAction={(row) => row.kind === 'missing' && (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" color="warning" onClick={() => openRegDialog(row.dateStr)}>
                Regularize
              </Button>
              <Button size="small" variant="outlined" onClick={() => setLeaveDialogDate(row.dateStr)}>
                Request Leave
              </Button>
            </Stack>
          )}
        />
      </Box>

      {/* Regularization History */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="h6" fontWeight={600}>My Regularization Requests</Typography>
        <Tooltip title="Pending requests are awaiting approval. Approved requests have updated your attendance record.">
          <IconButton size="small"><Info fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.200' }}>
              {['Date Requested For', 'Check In', 'Check Out', 'Status Requested', 'Reason', 'Status', 'Responded By', 'Rejection Reason'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {regularizations.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No regularization requests yet.</TableCell></TableRow>
            ) : pagedRegularizations.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{format(new Date(r.requestedDate), 'MMM dd, yyyy')}</TableCell>
                <TableCell>{r.requestedCheckIn}</TableCell>
                <TableCell>{r.requestedCheckOut || '—'}</TableCell>
                <TableCell>{r.requestedStatus}</TableCell>
                <TableCell sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }}>{r.reason}</TableCell>
                <TableCell>
                  <Chip label={r.status} color={regStatusColor(r.status) as any} size="small" />
                </TableCell>
                <TableCell>{r.approverName || '—'}</TableCell>
                <TableCell sx={{ color: 'error.main' }}>{r.rejectionReason || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {regularizations.length > 0 && <TablePagination {...regularizationPaginationProps} />}
      </TableContainer>

      {/* Regularization Submit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Attendance Regularization</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" required
                label="Date (past dates only)"
                type="date"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: yesterday }}
                value={form.requestedDate}
                onChange={e => setForm(f => ({ ...f, requestedDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" required
                label="Check-In Time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={form.requestedCheckIn}
                onChange={e => setForm(f => ({ ...f, requestedCheckIn: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small"
                label="Check-Out Time (optional)"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={form.requestedCheckOut}
                onChange={e => setForm(f => ({ ...f, requestedCheckOut: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select fullWidth size="small" required
                label="Attendance Status"
                value={form.requestedStatus}
                onChange={e => setForm(f => ({ ...f, requestedStatus: e.target.value }))}
              >
                <MenuItem value="Present">Present</MenuItem>
                <MenuItem value="Half Day">Half Day</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" required multiline rows={3}
                label="Reason for regularization"
                placeholder="Explain why attendance was not marked (e.g. forgot to check in, system issue, WFH, etc.)"
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitRegularization} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <RequestLeaveDialog
        open={!!leaveDialogDate}
        initialDate={leaveDialogDate || undefined}
        existingLeaveRequests={leaveRequests}
        onClose={() => setLeaveDialogDate(null)}
        onSubmitted={(msg) => {
          showMsg(msg, 'success');
          loadLeaveAndHolidays();
        }}
      />
    </Box>
  );
};

export default AttendanceTracking;
